import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/** 活动横幅数据（GET /api/banners?position=create 单条映射） */
interface CampaignBanner {
  id: number;
  title: string;
  description: string;
  cover: string;
  link: string;
  tag: string;
  endAt: number; // 毫秒时间戳；NaN = 运维平台未填结束时间（不限时活动）
}

/** 截止倒计时（秒级刷新）：无截止或已过期返回 null，横幅不显示倒计时区 */
const useCountdown = (endAt: number): string | null => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(endAt)) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  if (!Number.isFinite(endAt) || endAt <= now) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const remain = Math.floor((endAt - now) / 1000);
  const d = Math.floor(remain / 86400);
  const h = Math.floor((remain % 86400) / 3600);
  const m = Math.floor((remain % 3600) / 60);
  const s = remain % 60;
  return d > 0 ? `${d}天 ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/**
 * 创作页活动横幅（运营化打通，与会员页 Banner 同模式）：
 * - 数据源：运维平台「运营管理-轮播图」位置=创作页（admin_banners.position='create'），
 *   经 GET /api/banners?position=create 只读下发；后端已按上下线/投放期过滤，
 *   过期或下线后接口返回空 → 广告位整块隐藏，不阻塞创作主流程
 * - 视觉沿用会员页 PromoBanner 设计语言（渐变描边 + 封面镜像 + 左侧暗色渐变遮罩），
 *   结束时间有值时显示秒级倒计时
 */
export const CreateBanner: React.FC = () => {
  const [banner, setBanner] = useState<CampaignBanner | null>(null);
  const endAt = banner?.endAt ?? NaN;
  const countdown = useCountdown(endAt);

  // 拉取：组件实例级竞态防护（disposed），失败静默保持隐藏
  useEffect(() => {
    let disposed = false;
    fetch('/api/banners?position=create')
      .then((res) => (res.ok ? res.json() : null))
      .then((rows) => {
        if (disposed || !Array.isArray(rows) || rows.length === 0) return;
        const row = rows[0];
        const parsedEnd = row.endTime ? new Date(row.endTime).getTime() : NaN;
        setBanner({
          id: row.id,
          title: row.title || '',
          description: row.description || '',
          cover: row.cover || '',
          link: row.link || '',
          tag: row.tag || '',
          endAt: Number.isFinite(parsedEnd) ? parsedEnd : NaN,
        });
      })
      .catch(() => {
        // 拉取失败静默隐藏（广告位可降级，创作页主流程不受影响）
      });
    return () => {
      disposed = true;
    };
  }, []);

  if (!banner) return null;

  const inner = (
    <div className="relative h-[120px] rounded-[15px] overflow-hidden" style={{ backgroundColor: '#0C2231' }}>
      {banner.cover && (
        <img
          src={banner.cover}
          alt={banner.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
      )}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, rgba(1,28,43,0.92) 0%, rgba(1,28,43,0.55) 45%, rgba(1,28,43,0.05) 100%)' }}
      />
      <div className="relative h-full flex items-center justify-between gap-6 px-8">
        <div className="min-w-0">
          {banner.tag && (
            <span className="inline-block px-2 py-0.5 mb-2 rounded text-[11px] font-medium text-white bg-[#4FA4B1]/80">
              {banner.tag}
            </span>
          )}
          <div className="text-lg font-semibold text-white truncate">{banner.title}</div>
          {banner.description && <div className="mt-1 text-sm text-zinc-300 truncate">{banner.description}</div>}
        </div>
        <div className="flex items-center gap-6 shrink-0">
          {countdown && (
            <div className="text-right">
              <div className="text-[11px] text-zinc-400">距活动结束</div>
              <div className="text-xl font-semibold text-white tabular-nums" style={{ fontFamily: 'CKTKingkong, monospace' }}>
                {countdown}
              </div>
            </div>
          )}
          <div className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white">
            <ChevronRight size={18} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-8 rounded-2xl p-px" style={{ background: 'linear-gradient(-90deg, #4FA4B1, #FFFFFF)' }}>
      {banner.link ? (
        <a href={banner.link} target="_blank" rel="noreferrer" className="block cursor-pointer">
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
};
